'use client';
import { useCurrencyContext } from '@contexts/currency-context';
import { useBuildPackageBundle } from '@hooks/use-build-package-bundle';
import type { BaseProperty } from '@interfaces/expedia/property';
import { useSearchHotelStore } from '@store/search-hotel-store';
import { Button, Map02, Skeleton, Typography } from '@v2/ui';
import Link from 'next/link';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useGetHotels } from '../../hooks/use-hotels-list';

import { usePackagesParams } from '@hooks/use-packages-params';
import { applyMargin } from '@utils/currency';
import { buildHotelGa4Item } from '@v2/analytics/ecommerce';
import { trackGa4ItemListView, trackGa4ItemSelection } from '@v2/analytics/ga4';
import { HotelSearchField } from '../TopHotelFilterBar';
import * as HotelCard from './HotelCard';
import { HotelsListPlaceholder } from './HotelsListPlacehoder';
import { getHotelStayPricePerGuest } from '../../hotel-presentation';

const PER_PAGE = 10;

const getHotelDisplayPrice = ({
  hotelPrice,
  isBundle,
  lowestHotelPrice,
  packageGuests,
}: {
  hotelPrice: number;
  isBundle: boolean;
  lowestHotelPrice: number;
  packageGuests: number;
}) =>
  getHotelStayPricePerGuest({
    price: hotelPrice,
    lowestPrice: lowestHotelPrice,
    isBundle,
    guests: packageGuests,
  }) ?? 0;

type ResultsProps = {
  proximityName?: string;
  onHoverHotel?: (id: number | null) => void;
};

export function HotelsContent({ proximityName, onHoverHotel }: ResultsProps) {
  const { data, isLoading, isValidating, error, mutate, lowestHotelPrice } =
    useGetHotels();
  const isHotelsLoading = Boolean((isLoading || isValidating) && !data.length);

  return (
    <>
      <div className="mb-2 lg:mb-4">
        {isHotelsLoading ? (
          <Skeleton className="h-7 w-28" aria-label="Loading hotels" />
        ) : error && !data.length ? null : (
          <h3
            className="sr-only text-xl font-bold text-gray-900 lg:not-sr-only"
            aria-live="polite"
          >
            {data.length} {data.length === 1 ? 'hotel matches' : 'hotels match'}{' '}
            your search
          </h3>
        )}
        <p className="hidden text-sm text-gray-600 lg:mt-1 lg:block">
          Compare hotels near your event and find the right stay for your trip.
        </p>
      </div>
      <div className="mb-3 flex items-center gap-2 lg:hidden">
        <div className="min-w-0 flex-1">
          <HotelSearchField />
        </div>
        <Button
          type="button"
          variant="secondary"
          className="h-11 shrink-0 rounded-xl border border-gray-300 bg-white px-4 font-semibold text-gray-800 shadow-none hover:bg-gray-50"
          onClick={() =>
            useSearchHotelStore.setState({ isMapFullScreen: true })
          }
        >
          <Map02 size="18" className="mr-2 text-gray-700" />
          Map
        </Button>
      </div>
      {error && !data.length ? (
        <div
          role="alert"
          className="rounded-xl border bg-white p-6 text-center"
        >
          <p className="mb-3">Unable to load hotels. Please try again.</p>
          <button
            onClick={() => mutate()}
            className="font-semibold text-primary-600"
          >
            Try again
          </button>
        </div>
      ) : isHotelsLoading ? (
        <HotelsListPlaceholder />
      ) : data.length ? (
        <List
          list={data}
          lowestHotelPrice={lowestHotelPrice}
          proximityName={proximityName}
          onHoverHotel={onHoverHotel}
        />
      ) : (
        <div className="flex min-h-[262px] flex-col items-center justify-center rounded-xl border border-gray-200 bg-white p-6 text-center">
          <Typography size="txl" className="mb-2 font-bold text-gray-500">
            No hotels found
          </Typography>
          <p className="text-gray-500">
            Review your filters or try a different search
          </p>
        </div>
      )}
    </>
  );
}

function List({
  list,
  lowestHotelPrice,
  proximityName,
  onHoverHotel,
}: {
  list: BaseProperty[];
  lowestHotelPrice: number;
  proximityName?: string;
  onHoverHotel?: (id: number | null) => void;
}) {
  const [displayCount, setDisplayCount] = useState(PER_PAGE);

  const handleShowMore = () => {
    setDisplayCount(displayCount + PER_PAGE);
  };

  const { packageGuests, isBundle } = useBuildPackageBundle();
  const { currency, priceRate } = useCurrencyContext();
  const trackedLists = useRef(new Set<string>());
  const ga4Items = useMemo(
    () =>
      list.map((hotel, index) =>
        buildHotelGa4Item({
          hotelId: hotel.id,
          hotelName: hotel.name,
          index,
          price: Number(
            applyMargin(
              getHotelDisplayPrice({
                hotelPrice: hotel.price,
                isBundle,
                lowestHotelPrice,
                packageGuests,
              }),
              priceRate,
              currency
            )
          ),
        })
      ),
    [currency, isBundle, list, lowestHotelPrice, packageGuests, priceRate]
  );
  const trackingKey = ga4Items
    .map((item) => `${item.item_id}:${item.price}`)
    .join('|');

  useEffect(() => {
    if (!ga4Items.length || trackedLists.current.has(trackingKey)) return;
    trackedLists.current.add(trackingKey);
    trackGa4ItemListView({
      itemListId: 'hotel_results',
      itemListName: 'Hotel results',
      items: ga4Items,
    });
  }, [ga4Items, trackingKey]);

  return (
    <>
      <div className="flex flex-col gap-4">
        {list.slice(0, displayCount).map((hotel) => (
          <div
            key={hotel.id}
            id={`hotel-card-${hotel.id}`}
            className="scroll-mt-4"
            onMouseEnter={() => onHoverHotel?.(hotel.id)}
            onMouseLeave={() => onHoverHotel?.(null)}
            onFocus={() => onHoverHotel?.(hotel.id)}
            onBlur={() => onHoverHotel?.(null)}
          >
            <HotelItem
              {...hotel}
              lowestHotelPrice={lowestHotelPrice}
              proximityName={proximityName}
            />
          </div>
        ))}
      </div>
      {list.length > PER_PAGE && (
        <Button
          variant="ghost"
          className="mt-4 w-full bg-white"
          onClick={handleShowMore}
        >
          Show more
        </Button>
      )}
    </>
  );
}

export function HotelItem(
  props: BaseProperty & {
    lowestHotelPrice: number;
    className?: string;
    proximityName?: string;
    compact?: boolean;
    onItemClick?: (
      e: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
      val: BaseProperty
    ) => void;
  }
) {
  const { image, onItemClick, lowestHotelPrice } = props;
  const { packageGuests, isBundle } = useBuildPackageBundle();
  const { currency, priceRate } = useCurrencyContext();

  const { eventId, eventLink, packageType } = usePackagesParams();
  const handleClick = (
    elm: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    trackGa4ItemSelection({
      itemListId: 'hotel_results',
      itemListName: 'Hotel results',
      item: buildHotelGa4Item({
        hotelId: props.id,
        hotelName: props.name,
        price: Number(
          applyMargin(
            getHotelDisplayPrice({
              hotelPrice: props.price,
              isBundle,
              lowestHotelPrice,
              packageGuests,
            }),
            priceRate,
            currency
          )
        ),
      }),
    });
    onItemClick && onItemClick(elm, props);
  };

  return (
    <Link
      href={`/search-hotel/${props.id}?eventId=${eventId}&eventLink=${eventLink}&packageType=${packageType}`}
      onClick={handleClick}
      className="block w-full rounded-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-500"
    >
      <HotelCard.Card className={props.className}>
        <HotelCard.Content
          className={props.compact ? 'flex-row gap-3 p-4' : undefined}
        >
          <HotelCard.HotelImage
            image={image}
            name={props.name}
            compact={props.compact}
          />
          <HotelCard.Overview {...props} lowestPrice={lowestHotelPrice} />
        </HotelCard.Content>
      </HotelCard.Card>
    </Link>
  );
}
